import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { usePrivacy } from "@/context/PrivacyContext";
import { usePortfolio } from "@/context/PortfolioContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/format";
import { COLOR_CUTOFF_KEY, COLOR_CUTOFF_OPTIONS, getColorCutoff } from "@/lib/marketHours";
import { Plus, Pencil, Trash2, Copy, Moon, UserPlus, Mail, FileText, Percent, ChevronRight, EyeOff, Shield, ShieldOff, KeyRound, Check, Activity, Wifi, HelpCircle, CalendarClock, Palette } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function LinkCard({ icon: Icon, title, desc, onClick, testId }) {
    return (
        <Card onClick={onClick} className="p-5 cursor-pointer hover:border-primary/50 transition-colors flex items-center justify-between" data-testid={testId}>
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="w-4 h-4 text-primary" /></div>
                <div><h2 className="font-heading text-lg font-semibold leading-tight">{title}</h2><p className="text-sm text-muted-foreground">{desc}</p></div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </Card>
    );
}

function SubPortfolioManager() {
    const { subs, refreshSubs } = usePortfolio();
    const qc = useQueryClient();
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", description: "" });
    const [deleting, setDeleting] = useState(null);

    const open = (s) => { setEditing(s); setForm(s ? { name: s.name, description: s.description || "" } : { name: "", description: "" }); setDialog(true); };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        try {
            if (editing) await api.put(`/subportfolios/${editing.id}`, form);
            else await api.post("/subportfolios", form);
            toast.success("Saved"); setDialog(false); await refreshSubs(); qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); }
    };

    const del = async () => {
        try { await api.delete(`/subportfolios/${deleting.id}`); toast.success("Deleted"); await refreshSubs(); qc.invalidateQueries(); }
        catch (e) { toast.error(apiErr(e)); } finally { setDeleting(null); }
    };

    return (
        <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
                <div><h2 className="font-heading text-lg font-semibold">Sub-portfolios</h2><p className="text-sm text-muted-foreground">Organize your holdings into groups</p></div>
                <Button size="sm" onClick={() => open(null)} data-testid="add-subportfolio-btn"><Plus className="w-4 h-4 mr-1.5" />Add</Button>
            </div>
            <div className="space-y-2">
                {subs.map((s) => (
                    <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-border" data-testid={`sub-row-${s.name}`}>
                        <div><div className="font-medium">{s.name}</div>{s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}</div>
                        <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => open(s)} data-testid={`edit-sub-${s.name}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-loss" onClick={() => setDeleting(s)} data-testid={`delete-sub-${s.name}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent className="max-w-sm" data-testid="subportfolio-dialog">
                    <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} sub-portfolio</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="sub-name-input" /></div>
                        <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="sub-desc-input" /></div>
                    </div>
                    <DialogFooter><Button variant="ghost" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} data-testid="save-sub-btn">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete "{deleting?.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>Sub-portfolios with existing transactions cannot be deleted. Reassign or remove those transactions first.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-loss text-white hover:bg-loss/90">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

function UserManager({ currentUser }) {
    const qc = useQueryClient();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [removing, setRemoving] = useState(null);
    const invites = useQuery({ queryKey: ["invites"], queryFn: async () => (await api.get("/auth/invites")).data });
    const users = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

    const invite = async () => {
        if (!email.trim()) return;
        setLoading(true);
        try {
            const { data } = await api.post("/auth/invite", { email });
            await navigator.clipboard.writeText(data.invite_link).catch(() => {});
            toast.success("Invite created — link copied to clipboard");
            setEmail(""); qc.invalidateQueries({ queryKey: ["invites"] });
        } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
    };

    const setRole = async (u, role) => {
        try {
            await api.patch(`/users/${u.id}/role`, { role });
            toast.success(`${u.email} is now ${role}`);
            qc.invalidateQueries({ queryKey: ["users"] });
        } catch (e) { toast.error(apiErr(e)); }
    };

    const removeUser = async () => {
        try {
            await api.delete(`/users/${removing.id}`);
            toast.success(`Removed ${removing.email}`);
            qc.invalidateQueries({ queryKey: ["users"] });
        } catch (e) { toast.error(apiErr(e)); } finally { setRemoving(null); }
    };

    const copy = (link) => { navigator.clipboard.writeText(link); toast.success("Link copied"); };

    return (
        <Card className="p-5">
            <div className="flex items-center gap-2 mb-4"><UserPlus className="w-5 h-5 text-primary" /><div><h2 className="font-heading text-lg font-semibold">Users & Invitations</h2><p className="text-sm text-muted-foreground">Invite, promote and remove family-office members (admin only)</p></div></div>
            <div className="flex gap-2 mb-5">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="new.member@example.com" data-testid="invite-email-input" />
                <Button onClick={invite} disabled={loading} data-testid="create-invite-btn"><Mail className="w-4 h-4 mr-1.5" />Invite</Button>
            </div>

            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Members</div>
            <div className="space-y-1.5 mb-5">
                {(users.data || []).map((u) => {
                    const isSelf = currentUser?.id === u.id;
                    return (
                        <div key={u.id} className="flex items-center justify-between gap-2 text-sm p-2 rounded-lg border border-border" data-testid={`user-row-${u.email}`}>
                            <div className="min-w-0"><span className="font-medium">{u.name}</span> <span className="text-muted-foreground">· {u.email}</span>{isSelf && <span className="text-[10px] text-muted-foreground ml-1">(you)</span>}</div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                <Badge variant={u.role === "admin" ? "default" : "secondary"} className="text-[10px]" data-testid={`user-role-${u.email}`}>{u.role}</Badge>
                                {!isSelf && (u.role === "admin" ? (
                                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setRole(u, "user")} data-testid={`demote-${u.email}`}><ShieldOff className="w-3.5 h-3.5 mr-1" />Demote</Button>
                                ) : (
                                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setRole(u, "admin")} data-testid={`promote-${u.email}`}><Shield className="w-3.5 h-3.5 mr-1" />Make admin</Button>
                                ))}
                                {!isSelf && <Button variant="ghost" size="icon" className="h-7 w-7 text-loss" onClick={() => setRemoving(u)} data-testid={`remove-${u.email}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Pending invites</div>
            <div className="space-y-1.5">
                {(invites.data || []).filter((i) => !i.used).map((i) => (
                    <div key={i.id} className="flex items-center justify-between text-sm p-2 rounded-lg border border-border" data-testid={`invite-row-${i.email}`}>
                        <div className="min-w-0"><div className="font-medium truncate">{i.email}</div><div className="text-[10px] text-muted-foreground">expires {fmtDate(i.expires_at)}</div></div>
                        <Button variant="ghost" size="sm" onClick={() => copy(i.invite_link)} data-testid={`copy-invite-${i.email}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy link</Button>
                    </div>
                ))}
                {(invites.data || []).filter((i) => !i.used).length === 0 && <p className="text-sm text-muted-foreground">No pending invites.</p>}
            </div>

            <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Remove {removing?.email}?</AlertDialogTitle>
                        <AlertDialogDescription>This permanently deletes the member and ALL of their portfolio data (transactions, sub-portfolios, parameters). This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={removeUser} className="bg-loss text-white hover:bg-loss/90" data-testid="confirm-remove-user">Remove</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

function IntegrationsManager() {
    const qc = useQueryClient();
    const [val, setVal] = useState("");
    const [host, setHost] = useState("");
    const [saving, setSaving] = useState(false);
    const q = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/settings/integrations")).data });
    const configured = q.data?.rapidapi_configured;

    const save = async () => {
        if (!val.trim()) return toast.error("Paste a RapidAPI key first");
        setSaving(true);
        try {
            await api.put("/settings/integrations", { rapidapi_key: val.trim() });
            toast.success("RapidAPI key saved");
            setVal("");
            qc.invalidateQueries({ queryKey: ["integrations"] });
            qc.invalidateQueries({ queryKey: ["ratings"] });
        } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
    };

    const saveHost = async () => {
        const h = (host || q.data?.rapidapi_host || "").trim();
        setSaving(true);
        try {
            await api.put("/settings/integrations", { rapidapi_host: h });
            toast.success("API host saved");
            setHost("");
            qc.invalidateQueries({ queryKey: ["integrations"] });
            qc.invalidateQueries({ queryKey: ["ratings"] });
        } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
    };

    const clear = async () => {
        setSaving(true);
        try {
            await api.put("/settings/integrations", { rapidapi_key: "" });
            toast.success("RapidAPI key removed");
            setVal("");
            qc.invalidateQueries({ queryKey: ["integrations"] });
            qc.invalidateQueries({ queryKey: ["ratings"] });
        } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
    };

    return (
        <Card className="p-5" data-testid="integrations-card">
            <div className="flex items-center gap-2 mb-2"><KeyRound className="w-5 h-5 text-primary" /><div><h2 className="font-heading text-lg font-semibold">Integrations — Seeking Alpha Ratings</h2><p className="text-sm text-muted-foreground">Paste your RapidAPI key to power the Ratings view on Holdings (admin only)</p></div></div>
            <div className="flex items-center gap-2 mb-3 text-sm">
                {configured ? (
                    <Badge className="gap-1" data-testid="rapidapi-status"><Check className="w-3 h-3" />Configured</Badge>
                ) : (
                    <Badge variant="secondary" data-testid="rapidapi-status">Not configured</Badge>
                )}
                {configured && q.data?.rapidapi_key_masked && <span className="num text-muted-foreground" data-testid="rapidapi-masked">{q.data.rapidapi_key_masked}</span>}
            </div>
            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">RapidAPI key</Label>
                <div className="flex gap-2">
                    <Input type="password" autoComplete="off" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Paste RapidAPI key…" data-testid="rapidapi-key-input" />
                    <Button onClick={save} disabled={saving} data-testid="save-rapidapi-btn">Save</Button>
                    {configured && <Button variant="ghost" onClick={clear} disabled={saving} className="text-loss" data-testid="clear-rapidapi-btn">Remove</Button>}
                </div>
            </div>
            <div className="space-y-2 mt-4">
                <Label className="text-xs text-muted-foreground">RapidAPI host</Label>
                <div className="flex gap-2">
                    <Input value={host || q.data?.rapidapi_host || ""} onChange={(e) => setHost(e.target.value)} placeholder="seeking-alpha-api1.p.rapidapi.com" data-testid="rapidapi-host-input" />
                    <Button variant="outline" onClick={saveHost} disabled={saving} data-testid="save-host-btn">Save host</Button>
                </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Currently the <span className="font-medium text-foreground">Wall St rating</span> is live on free RapidAPI tiers. Quant Rating &amp; Factor Grades are premium-locked at the Seeking Alpha source; they'll populate automatically if you configure a host/plan that returns them unlocked.</p>
        </Card>
    );
}

function YahooAuthManager() {
    const qc = useQueryClient();
    const [t, setT] = useState("");
    const [y, setY] = useState("");
    const [saving, setSaving] = useState(false);
    const [help, setHelp] = useState(false);
    const [fresh, setFresh] = useState(null);
    const [checking, setChecking] = useState(false);
    const [diag, setDiag] = useState(null);
    const [testing, setTesting] = useState(false);
    const q = useQuery({ queryKey: ["yahoo-auth"], queryFn: async () => (await api.get("/settings/yahoo-auth")).data });
    const s = q.data || {};

    const save = async () => {
        if (!t.trim() || !y.trim()) return toast.error("Paste both the T and Y cookies");
        setSaving(true);
        try {
            const { data } = await api.put("/settings/yahoo-auth", { cookie_t: t.trim(), cookie_y: y.trim() });
            if (data.logged_in) toast.success(`Connected — tier: ${data.tier || "authenticated"}`);
            else toast.warning("Cookies saved, but Yahoo did not confirm a logged-in session. Double-check the values (or they may be expired).");
            setT(""); setY("");
            qc.invalidateQueries({ queryKey: ["yahoo-auth"] });
        } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
    };

    const clear = async () => {
        setSaving(true);
        try {
            await api.delete("/settings/yahoo-auth");
            toast.success("Yahoo session disconnected");
            setT(""); setY(""); setFresh(null);
            qc.invalidateQueries({ queryKey: ["yahoo-auth"] });
        } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
    };

    const checkFreshness = async () => {
        setChecking(true);
        try {
            const { data } = await api.get("/market/freshness", { params: { ticker: "AAPL" } });
            setFresh(data);
        } catch (e) { toast.error(apiErr(e)); } finally { setChecking(false); }
    };

    const testConnection = async () => {
        setTesting(true);
        setDiag(null);
        try {
            const { data } = await api.post("/settings/yahoo-auth/test");
            setDiag(data);
        } catch (e) { toast.error(apiErr(e)); } finally { setTesting(false); }
    };

    const ageLabel = (sec) => {
        if (sec == null) return "unknown";
        if (sec < 90) return `${sec}s ago (essentially real-time)`;
        const m = Math.round(sec / 60);
        return `${m} min ago${m >= 10 ? " (delayed)" : ""}`;
    };

    return (
        <Card className="p-5" data-testid="yahoo-auth-card">
            <div className="flex items-center gap-2 mb-2">
                <Wifi className="w-5 h-5 text-primary" />
                <div>
                    <h2 className="font-heading text-lg font-semibold">Market Data — Yahoo Finance Session</h2>
                    <p className="text-sm text-muted-foreground">Connect your Yahoo Finance subscription to route quotes through your logged-in session (admin only)</p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
                {s.logged_in ? (
                    <Badge className="gap-1" data-testid="yahoo-status"><Check className="w-3 h-3" />Connected</Badge>
                ) : s.configured ? (
                    <Badge variant="destructive" data-testid="yahoo-status">Cookies set · not logged in</Badge>
                ) : (
                    <Badge variant="secondary" data-testid="yahoo-status">Not connected</Badge>
                )}
                {s.logged_in && s.tier && <Badge variant="outline" className="capitalize" data-testid="yahoo-tier">Tier: {s.tier}</Badge>}
                {s.configured && <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-loss" onClick={clear} disabled={saving} data-testid="disconnect-yahoo-btn">Disconnect</Button>}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Cookie "T"</Label>
                    <Input type="password" autoComplete="off" value={t} onChange={(e) => setT(e.target.value)} placeholder="Paste T cookie value…" data-testid="yahoo-cookie-t-input" />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Cookie "Y"</Label>
                    <Input type="password" autoComplete="off" value={y} onChange={(e) => setY(e.target.value)} placeholder="Paste Y cookie value…" data-testid="yahoo-cookie-y-input" />
                </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
                <Button onClick={save} disabled={saving} data-testid="save-yahoo-btn">Connect</Button>
                <Button variant="outline" onClick={checkFreshness} disabled={checking} data-testid="check-freshness-btn">
                    <Activity className="w-4 h-4 mr-1.5" />{checking ? "Checking…" : "Test data freshness"}
                </Button>
                {s.configured && (
                    <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="test-yahoo-btn">
                        <Wifi className="w-4 h-4 mr-1.5" />{testing ? "Testing…" : "Test connection"}
                    </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setHelp((v) => !v)} data-testid="yahoo-help-toggle"><HelpCircle className="w-4 h-4 mr-1" />How to get cookies</Button>
            </div>

            {diag && (
                <div className="mt-4 p-3 rounded-lg border border-border bg-muted/30 text-sm" data-testid="yahoo-diag-result">
                    <div className="font-medium mb-1">Connection diagnostic (this server)</div>
                    <div className="text-muted-foreground space-y-0.5">
                        <div>Cookies stored: <span className="text-foreground">{String(diag.has_cookies)}</span></div>
                        <div>set_login_cookies: <span className="text-foreground">{String(diag.set_login_cookies ?? diag.set_login_cookies_error ?? "—")}</span></div>
                        <div>check_login: <span className="text-foreground">{String(diag.check_login ?? diag.check_login_error ?? "—")}</span></div>
                        <div>tier: <span className="text-foreground">{String(diag.tier ?? diag.tier_error ?? "—")}</span></div>
                        <div>live quote: <span className="text-foreground">{diag.live_quote ? `$${diag.live_quote.price} · ${diag.live_quote.source}` : (diag.live_quote_error || "—")}</span></div>
                        <div>latest bar age: <span className="text-foreground">{diag.freshness ? ageLabel(diag.freshness.age_seconds) : "—"}</span> · market: <span className="text-foreground">{diag.freshness?.market_state || "—"}</span></div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">If check_login is false but the live quote source says "subscriber/authenticated", entitlements still apply. If it says "delayed", Yahoo is rejecting the session from this server's IP.</p>
                </div>
            )}

            {fresh && (
                <div className="mt-4 p-3 rounded-lg border border-border bg-muted/30 text-sm" data-testid="freshness-result">
                    <div className="font-medium mb-1">Freshness probe · {fresh.ticker}</div>
                    <div className="text-muted-foreground space-y-0.5">
                        <div>Last price: <span className="num text-foreground">{fresh.last_price != null ? `$${fresh.last_price}` : "—"}</span></div>
                        <div>Latest bar: <span className="text-foreground">{ageLabel(fresh.age_seconds)}</span></div>
                        <div>Exchange declared delay: <span className="text-foreground">{fresh.delayed_by_seconds != null ? `${Math.round(fresh.delayed_by_seconds / 60)} min` : "unknown"}</span></div>
                        <div>Market state: <span className="text-foreground">{fresh.market_state || "unknown"}</span></div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Run this during US market hours (9:30am–4:00pm ET) for a meaningful delay reading.</p>
                </div>
            )}

            {help && (
                <div className="mt-4 p-3 rounded-lg border border-border bg-muted/20 text-sm space-y-1.5" data-testid="yahoo-help">
                    <div className="font-medium">Copy your Yahoo session cookies</div>
                    <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
                        <li>In your browser, log in at <span className="text-foreground">finance.yahoo.com</span> with your subscriber account.</li>
                        <li>Open Developer Tools — <span className="text-foreground">F12</span> (or Cmd+Option+I on Mac).</li>
                        <li>Go to the <span className="text-foreground">Application</span> tab (Chrome) or <span className="text-foreground">Storage</span> tab (Firefox).</li>
                        <li>Under <span className="text-foreground">Cookies → https://finance.yahoo.com</span>, find the cookies named <span className="text-foreground">T</span> and <span className="text-foreground">Y</span>.</li>
                        <li>Copy each value and paste them into the fields above, then click Connect.</li>
                    </ol>
                    <p className="text-xs text-muted-foreground">Cookies are stored server-side and used only for market-data requests. They expire periodically — reconnect if the status turns red.</p>
                </div>
            )}
        </Card>
    );
}

function DateFixManager() {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [changes, setChanges] = useState(null);

    const load = async () => {
        setOpen(true); setLoading(true); setChanges(null);
        try { const { data } = await api.get("/transactions/reinterpret-dates/preview"); setChanges(data.changes); }
        catch (e) { toast.error(apiErr(e)); setOpen(false); } finally { setLoading(false); }
    };

    const apply = async () => {
        setApplying(true);
        try {
            const ids = changes.map((c) => c.id);
            const { data } = await api.post("/transactions/reinterpret-dates/apply", { ids });
            toast.success(`Updated ${data.updated} transaction date${data.updated === 1 ? "" : "s"}`);
            setOpen(false); qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); } finally { setApplying(false); }
    };

    return (
        <Card className="p-5" data-testid="date-fix-card">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <CalendarClock className="w-5 h-5 text-primary" />
                    <div>
                        <h2 className="font-heading text-lg font-semibold leading-tight">Fix imported dates</h2>
                        <p className="text-sm text-muted-foreground">Re-read ambiguous numeric dates as day/month (European). Use this if imported dates landed on the wrong day or month.</p>
                    </div>
                </div>
                <Button variant="outline" onClick={load} data-testid="date-fix-review-btn">Review</Button>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="date-fix-dialog">
                    <DialogHeader><DialogTitle className="font-heading">Re-interpret dates as day/month</DialogTitle></DialogHeader>
                    {loading ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">Scanning transactions…</div>
                    ) : changes && changes.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="date-fix-empty">No ambiguous dates found — nothing to change.</div>
                    ) : changes ? (
                        <>
                            <p className="text-sm text-muted-foreground">These {changes.length} transaction{changes.length === 1 ? "" : "s"} will have their day and month swapped. Review carefully — this changes stored dates.</p>
                            <div className="max-h-72 overflow-y-auto text-xs space-y-1" data-testid="date-fix-list">
                                {changes.map((c) => (
                                    <div key={c.id} className="flex items-center justify-between gap-2 border-b border-border/40 py-1">
                                        <span className="num font-medium">{c.ticker} · {c.transaction_type}</span>
                                        <span className="flex items-center gap-2">
                                            <span className="text-loss line-through">{fmtDate(c.current_date)}</span>
                                            <ChevronRight className="w-3 h-3 text-muted-foreground" />
                                            <span className="text-gain font-semibold">{fmtDate(c.new_date)}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : null}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        {changes && changes.length > 0 && (
                            <Button onClick={apply} disabled={applying} data-testid="date-fix-apply-btn">
                                {applying ? "Applying…" : `Swap ${changes.length} date${changes.length === 1 ? "" : "s"}`}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

function MarketColorSetting() {
    const [cutoff, setCutoff] = useState(getColorCutoff);
    const change = (v) => {
        setCutoff(v);
        try { localStorage.setItem(COLOR_CUTOFF_KEY, v); } catch { /* storage unavailable */ }
        window.dispatchEvent(new Event("market-color-cutoff"));
    };
    return (
        <Card className="p-5" data-testid="market-color-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3"><Palette className="w-5 h-5 text-primary shrink-0" /><div><div className="font-medium">Market colours</div><div className="text-sm text-muted-foreground">Dashboard market figures show red/green from the 9:30am New York open, keep the day's colour after the close, then turn black at the time you choose (next day, your local time)</div></div></div>
                <Select value={cutoff} onValueChange={change}>
                    <SelectTrigger className="w-[220px]" data-testid="market-color-cutoff-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {COLOR_CUTOFF_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
        </Card>
    );
}

export default function Settings() {
    const { user } = useAuth();
    const { theme, toggle } = useTheme();
    const { autoLock, setAutoLock, autoLockMin, setAutoLockMin, lockOnBlur, setLockOnBlur } = usePrivacy();
    const navigate = useNavigate();
    return (
        <div className="space-y-6 max-w-3xl">
            <div><h1 className="font-heading text-2xl font-bold tracking-tight">Settings</h1><p className="text-sm text-muted-foreground">Preferences, sub-portfolios and users</p></div>

            <Card className="p-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3"><Moon className="w-5 h-5 text-primary" /><div><div className="font-medium">Dark mode</div><div className="text-sm text-muted-foreground">Toggle light / dark appearance</div></div></div>
                    <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="settings-theme-switch" />
                </div>
            </Card>

            <Card className="p-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3"><EyeOff className="w-5 h-5 text-primary" /><div><div className="font-medium">Auto-lock values</div><div className="text-sm text-muted-foreground">Automatically hide dollar values after a period of inactivity</div></div></div>
                    <div className="flex items-center gap-3">
                        <Select value={String(autoLockMin)} onValueChange={(v) => setAutoLockMin(Number(v))} disabled={!autoLock}>
                            <SelectTrigger className="w-[130px]" data-testid="autolock-minutes-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {[1, 2, 5, 10, 15, 30].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Switch checked={autoLock} onCheckedChange={setAutoLock} data-testid="settings-autolock-switch" />
                    </div>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-3"><EyeOff className="w-5 h-5 text-primary" /><div><div className="font-medium">Lock on tab switch</div><div className="text-sm text-muted-foreground">Instantly hide values when you switch tabs or minimize the window</div></div></div>
                    <Switch checked={lockOnBlur} onCheckedChange={setLockOnBlur} data-testid="settings-lockonblur-switch" />
                </div>
            </Card>

            <MarketColorSetting />

            <SubPortfolioManager />
            <DateFixManager />
            <LinkCard icon={Percent} title="Parameters" desc="Inflation rate & other analytic assumptions" onClick={() => navigate("/parameters")} testId="settings-parameters-link" />
            <LinkCard icon={FileText} title="Tax Reports" desc="Realized gains & dividend income by year" onClick={() => navigate("/tax")} testId="settings-tax-link" />
            {user?.role === "admin" && <UserManager currentUser={user} />}
            {user?.role === "admin" && <YahooAuthManager />}
            {user?.role === "admin" && <IntegrationsManager />}

            <Card className="p-5">
                <h2 className="font-heading text-lg font-semibold mb-2">Account</h2>
                <div className="text-sm space-y-1"><div><span className="text-muted-foreground">Name:</span> {user?.name}</div><div><span className="text-muted-foreground">Email:</span> {user?.email}</div><div><span className="text-muted-foreground">Role:</span> {user?.role}</div></div>
            </Card>

            <p className="text-xs text-muted-foreground">Currency: USD · Market data: Yahoo Finance (delayed). Data may be delayed up to ~15 minutes; timestamps are shown throughout the app.</p>
        </div>
    );
}
